import { wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX } from "react";

// Matches the AdequacyGainsPercentEnum on the server
enum GainsPercentEnum {
  All = 1,
  Zero = 2,
  Nonzero = 3,
}

// Matches NextClosestImpactFilterEnum on the API, used to filter NC Impact Providers
enum NcpEnum {
  All = 1,
  NextClosest = 2,
  NotNextClosest = 3,
}


// Network Provider Fields
// Abbreviated to make repeated references shorter.
// Values need to match property names of NetworkServicingProviderModel.cs.
enum NPF {
  NPI = "NPI",
  DisplayName = "DisplayName",
  Prefix = "Prefix",
  FirstName = "FirstName",
  MiddleName = "MiddleName",
  LastName = "LastName",
  Suffix = "Suffix",
  Credentials = "Credentials",
  Gender = "Gender",
  SpecialtyCode = "SpecialtyCode",
  Specialty = "Specialty",
  Address = "Address",
  Address2 = "Address2",
  City = "City",
  State = "State",
  Zip = "Zip",
  Phone = "Phone",
  Fax = "Fax",
  Latitude = "Latitude",
  Longitude = "Longitude",
  GeoInfo = "GeoInfoDisplay",
  CountyFIPS = "CountyFIPS",
  CountySSA = "CountySSA",
  CountyName = "CountyName",
  CountyState = "CountyState",
  ServesPercent = "ServesPercent",
  DaysSinceLastAttested = "DaysSinceLastAttested",
  NppesCertificationDate = "NppesCertificationDate",
  NppesLastUpdatedDate = "NppesLastUpdatedDate",
  DeactivatedNPI = "DeactivatedNPI",
  MedicareOptOut = "MedicareOptOut",
  OIGExcluded = "OIGExcluded",
  OIGWaiver = "OIGWaiver",
  Precluded = "Precluded",
  InvalidNPI = "InvalidNPI",
  MissingNPI = "MissingNPI",
  TIN = "TIN",
  TaxonomyCode = "TaxonomyCode",
  AcceptingNewPatients = "AcceptingNewPatients",
  EmploymentStatus = "EmploymentStatus",
  ContractType = "ContractType",
  UnderContract = "UnderContract",
  MedGroupAffiliation = "MedGroupAffiliation",
  MedicareCCN = "MedicareCCN",
  MedicareCertifiedBeds = "MedicareCertifiedBeds",
  AcceptsMedicareAssignment = "AcceptsMedicareAssignment",
  ExternZip = "ExternZip",
  ExternSpecialtyCode = "ExternSpecialtyCode",
  ExternRowId = "ExternRowId",
  ExternProviderId = "ExternProviderId",
  ExternLocationId = "ExternLocationId",
  EntityType = "EntityType",
  OrganizationName = "OrganizationName",
  Score = "Score",
  MipsScore = "MipsScore",
  CommercialClaimsVolume = "CommercialClaimsVolumeDisplay",
  MedicaidClaimsVolume = "MedicaidClaimsVolumeDisplay",
  MedicareClaimsVolume = "MedicareClaimsVolumeDisplay",
  VAClaimsVolume = "VAClaimsVolumeDisplay",
  TricareClaimsVolume = "TricareClaimsVolumeDisplay",
  TotalClaimsVolume = "TotalClaimsVolumeDisplay",
  LocationConfidenceScore = "LocationConfidenceScoresDisplay",
  EffectivenessRank = "EffectivenessRank",
  EfficiencyRank = "EfficiencyRank",
  MotiveScore = "MotiveScore",
  CommercialNewPatientClaims = "CommercialNewPatientClaims",
  MedicaidNewPatientClaims = "MedicaidNewPatientClaims",
  MedicareNewPatientClaims = "MedicareNewPatientClaims",
  TotalNewPatientClaims = "TotalNewPatientClaims",
  VANewPatientClaims = "VANewPatientClaims",
  TricareNewPatientClaims = "TricareNewPatientClaims",
  CareJourneyScoresCostScore = "CareJourneyScoresCostScore",
  CareJourneyScoresQualityScore = "CareJourneyScoresQualityScore"
}

interface NetworkProvidersComparisonTableData {
  [wafflesKeyColumnName]: string;
  address: JSX.Element;
  comparisonProviderType: JSX.Element;
  displayName: JSX.Element;
  medicareProviderOptOut: JSX.Element;
  providerDaysSinceLastAttested: JSX.Element;
  servesPercent: JSX.Element;
}

interface ImpactProviderTableData {
  [wafflesKeyColumnName]: string;
  acceptingNewPatients: string;
  acceptsMedicareAssignment: string;
  address: JSX.Element;
  availability: JSX.Element;
  commercialNewPatientClaims: string;
  contractType: string;
  countyFIPS: string;
  countySSA: string;
  countyName?: string;
  countyState?: string;
  credentials: string;
  name: JSX.Element;
  daysSinceLastAttested: string;
  isRecentlyAttested: string;
  lastAttestedDate: string;
  effectivenessRank?: string;
  efficiencyRank?: string;
  employmentStatus: string;
  exclusionVerifiedSources: JSX.Element;
  externLocationId: string;
  externProviderId: string;
  externRowId: string;
  externSpecialtyCode: string;
  externZip: string;
  firstName: string;
  gainsPercent: string;
  gender: string;
  geoInfoDisplay: string;
  impactsPercent: string;
  lastName: string;
  latitude: number;
  letterOfIntent: string;
  longitude: number;
  medGroupAffiliation: string;
  medicaidNewPatientClaims: string;
  medicareCCN: string;
  medicareCertifiedBeds: number;
  medicareNewPatientClaims: string;
  middleName: string;
  mipsScore: string;
  nppesCertificationDate: string;
  nppesLastUpdatedDate: string;
  prefix: string;
  projectionReason: JSX.Element;
  projectionReasonDescription: JSX.Element;
  lastUpdate: JSX.Element;
  lastUpdateBy: JSX.Element;
  servesPercent: string;
  specialty: string;
  specialtyCode: string;
  suffix: string;
  taxonomyCode: string;
  underContract: string;
  totalNewPatientClaims: string;
  tricareNewPatientClaims: string;
  commercialClaimsVolumeDisplay: string;
  medicaidClaimsVolumeDisplay: string;
  medicareClaimsVolumeDisplay: string;
  vaClaimsVolumeDisplay: string;
  tricareClaimsVolumeDisplay: string;
  totalClaimsVolumeDisplay: string;
  locationConfidenceScoresDisplay: string;
  motiveScore: string;
  vaNewPatientClaims: string;
  careJourneyScoresCostScore: string;
  careJourneyScoresQualityScore: string;
}

type ImpactProviderTableDataWithCargo = {
  [cargoKey: string]: string | number | JSX.Element;
} & ImpactProviderTableData;

interface NextClosestImpactProviderTableData extends ImpactProviderTableData {
  [wafflesKeyColumnName]: string;
  /** Provider or Facility name. Includes NPI.
   */
  displayName: JSX.Element;
  /** Includes phone number
   */
  address: JSX.Element;
  /** Count for which the provider is the next closest provider for the member
   */
  totalMemberCount: number;
  /** Count for which the provider is closer than the member's next closest network provider
   */
  memberCount: number;
  /** avg time for which the provider is the next closest provider for the member
   */
  totalAverageTime: number;
  /**  avg time for which the provider is closer than the closest network provider for the member, but is not necessarily the member's closest provider
   */
  averageTime: number;
  /**  avg dist for which the provider is closer than the closest network provider for the member, but is not necessarily the member's closest provider
   */
  averageDistance: number;
  /** avg dist for which the provider is the next closest provider for the member
   */
  totalAverageDistance: number;
}

interface NetNewServicingProviderTableData extends ImpactProviderTableData {
  [wafflesKeyColumnName]: string;
  displayName: JSX.Element;
  npi: string;
}

type NextClosestImpactProviderTableDataWithCargo = {
  [cargoKey: string]: string | number | JSX.Element;
} & NextClosestImpactProviderTableData;

export {
  GainsPercentEnum,
  ImpactProviderTableData,
  ImpactProviderTableDataWithCargo,
  NcpEnum,
  NetworkProvidersComparisonTableData,
  NextClosestImpactProviderTableData,
  NextClosestImpactProviderTableDataWithCargo,
  NPF,
  NetNewServicingProviderTableData
};
